import "./AHUDesigner.css";
import * as THREE from "three";
import Viewport from "./Viewport";
import { ContactShadows, OrbitControls, OrthographicCamera, PerspectiveCamera } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useReducer, useRef, useState } from "react";
import { COMPONENT_TYPES, DEFAULT_EMPTY_THICKNESS, DIRECTION_EXTRACT, DIRECTION_SUPPLY, COMPONENT_ALIGNMENT, TOOL_MODE, DOOR_HINGE, ACCESS_SIDE } from "./Constants";
import Unit from "./Components/Unit";
import frameReducer from "./Store/frameReducer";
import { calculateAttached, fromMM, round, toggleHasFrame } from "./Utils/frameUtils";
import { useStore } from "./Store/zustandStore";
import Dimensions from "./Components/Dimensions";
import Header from "./Header";

function convert(data) {
  if (!data) return [];

  const { frames, ventilationUnitHeight, frameThickness } = data;

  let position = [0, fromMM(ventilationUnitHeight) / 2 + fromMM(100) + fromMM(frameThickness), 0];
  let newFrames = [];
  let totalLength = 0;

  let currentLeftId = null;
  let currentRightId = null;

  let nextLeftId = THREE.MathUtils.generateUUID();
  let nextRightId = THREE.MathUtils.generateUUID();

  let prevLeftAttachedId = null;
  let prevRightAttachedId = null;

  for (let i = 0; i < frames.length; i++) {
    currentLeftId = nextLeftId;
    currentRightId = nextRightId;

    nextLeftId = i == frames.length - 1 ? null : THREE.MathUtils.generateUUID();
    nextRightId = THREE.MathUtils.generateUUID();

    const { component, componentInstance, componentHeight, componentLength, componentWidth, height, length, width, guid, sectionId, doorType, doorLength, rows, inletType, accessSide } = frames[i];

    position[0] += fromMM(length / 2) + fromMM(frameThickness);

    const frame = {
      // id: THREE.MathUtils.generateUUID(),
      id: guid,
      sectionId: sectionId,
      position: [...position],
      length: length,
      height: height,
      width: width,
      connections: connections[0].map((c, idx) => {
        return {
          ...c,
          id: c.side == COMPONENT_ALIGNMENT.LEFT ? currentLeftId : currentRightId,
          attachedTo: c.side == COMPONENT_ALIGNMENT.LEFT ? prevRightAttachedId : nextLeftId,
        };
      }),
      hasLeftFrame: true,
      hasRightFrame: true,
      hasFrame: true,
      hasFloor: true,
      hasCeiling: true,
      hasLeftWall: false, // i == 0,
      hasRightWall: false,
      hasFlange: i == 0 || component == COMPONENT_TYPES.ESP || component == COMPONENT_TYPES.FAN,
      flangeAlignment: i == 0 ? -1 : 1,
      accessSide: accessSide == "LEFT" ? ACCESS_SIDE.LEFT : ACCESS_SIDE.RIGHT,
      door: {
        visible: true,
        //doorLength
        length: doorLength,
        // : 250 > length
        // ? length
        // : 250,
        //hinge: component == COMPONENT_TYPES.ESP ? DOOR_HINGE.LIFT : DOOR_HINGE.LEFT,
        hinge: doorType == "Louvre" ? DOOR_HINGE.LIFT : DOOR_HINGE.LEFT,
        louvered: doorType == "Louvre",
      },
      component: component,
      componentAlignment:
        component == COMPONENT_TYPES.FILTER ||
        component == COMPONENT_TYPES.HEPA_FILTER ||
        component == COMPONENT_TYPES.CARBON_FILTER_V ||
        component == COMPONENT_TYPES.CARBON_FILTER ||
        component == COMPONENT_TYPES.CARBON_FILTER_C ||
        component == COMPONENT_TYPES.BAG_FILTER ||
        component == COMPONENT_TYPES.UV_LIGHTS
          ? COMPONENT_ALIGNMENT.LEFT
          : component == COMPONENT_TYPES.FAN
          ? COMPONENT_ALIGNMENT.RIGHT
          : undefined,
      componentInstance: componentInstance,
      baseFrame: {
        visible: true,
        height: 100,
      },
      rows: rows ?? [1],
      flange:{
        typeOfFlange: inletType
      }
    };

    //prevLeftAttachedId = i == frames.length - 2 ? null : currentLeftAttachedId;
    prevRightAttachedId = currentRightId;

    switch (component) {
      case COMPONENT_TYPES.ESP:
        {
          const { hasAutoWasher, controlBoxWidth, controlBoxLength, controlBoxHeight, noOfRows, unitsPerRow } = frames[i];
          frame.esp = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            hasAutoWasher: hasAutoWasher,
            controlBoxWidth: controlBoxWidth,
            controlBoxLength: controlBoxLength,
            controlBoxHeight: controlBoxHeight,
            rows: noOfRows,
            columns: unitsPerRow,
          };
        }

        break;
      case COMPONENT_TYPES.UV_LIGHTS:
        {
          const { unitsPerRow, noOfRows, controlBoxWidth, controlBoxLength, controlBoxHeight } = frames[i];
          frame.uv = {
            tubeWidth: componentWidth,
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: noOfRows,
            columns: unitsPerRow,
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
          };
        }
        break;

      case COMPONENT_TYPES.ODOUR_NEUTRALISER:
        {
          const { controlBoxWidth, controlBoxLength, controlBoxHeight } = frames[i];
          frame.odour = {
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
          };
        }
        break;
      case COMPONENT_TYPES.OZONE:
        {
          const { controlBoxWidth, controlBoxLength, controlBoxHeight } = frames[i];
          frame.ozone = {
            boxWidth: controlBoxWidth,
            boxLength: controlBoxLength,
            boxHeight: controlBoxHeight,
          };
        }
        break;

      case COMPONENT_TYPES.FILTER:
      case COMPONENT_TYPES.BAG_FILTER:
      case COMPONENT_TYPES.HEPA_FILTER:
      case COMPONENT_TYPES.CARBON_FILTER:
        {
          const { arrayRows, arrayColumns, component } = frames[i];
          frame.filter = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            component: component,
          };
        }
        break;
      case COMPONENT_TYPES.CARBON_FILTER_V:
        {
          const { arrayRows, arrayColumns, component } = frames[i];
          frame.carbonFilter = {
            width: COMPONENT_TYPES.CARBON_FILTER_V ? width : componentWidth,
            length: COMPONENT_TYPES.CARBON_FILTER_V ? length : componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            component: component,
            depth:componentLength
          };
        }
        break;
      case COMPONENT_TYPES.CARBON_FILTER_C:
        {
          const { arrayRows, arrayColumns, noOfCylinders, component, arrayCylinders } = frames[i];
          frame.carbonCylindricalFilter = {
            width: componentWidth,
            length: componentLength,
            height: componentHeight,
            rows: arrayRows,
            columns: arrayColumns,
            numberOfCylinders: noOfCylinders,
            component: component,
            numberOfCylinders: arrayCylinders,
            plateWidth: width,
            plateHeight: height,
            cylinderLength:componentLength
          };
        }
        break;
      case COMPONENT_TYPES.FAN: {
        const { fanAngle,fanWidth,fanOutletHeight,fanOutletWidth, fanOutletLength, fanHeight, fanChannelHeight, baseChannelHeight,isolatorHeight  } = frames[i];
        frame.fan = {
          angle: fanAngle,
          outletHeight:fanOutletHeight > 0 ? fanOutletHeight : null,
          outletLength:fanOutletLength > 0 ? fanOutletLength : null,
          size:fanHeight > 0 ? fanHeight : null,
          width:fanOutletWidth > 0 ? fanOutletWidth : null,
          distanceFromTop: fanHeight > 0 ? (height - (fanHeight + fanChannelHeight + baseChannelHeight + isolatorHeight)) : null,
          mountingFrameLength: length * 0.8,
          fanChannelHeight: fanChannelHeight,
          baseChannelHeight: baseChannelHeight,
          isolatorHeight: isolatorHeight
        };

        frame.flangeAlignment = fanAngle == 90 ? COMPONENT_ALIGNMENT.TOP : COMPONENT_ALIGNMENT.RIGHT;
        break;
      }
      default:
        break;
    }

    newFrames.push(frame);
    position[0] += fromMM(length / 2) + fromMM(frameThickness);

    position[0] = round(position[0], 3);

    totalLength += length;
  }

  for (let i = 0; i < newFrames.length; i++) {
    const frame = newFrames[i];
    frame.position[0] -= fromMM(totalLength / 2);
  }

  // return calculateAttached(newFrames);

  // let finalFrames = calculateAttached(newFrames);

  const sectionIds = [...new Set(newFrames.filter((f) => f.sectionId != null).map((f) => f.sectionId))];

  for (let i = 0; i < sectionIds.length; i++) {
    const sectionFrames = newFrames.filter((f) => f.sectionId === sectionIds[i]);

    newFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.LEFT, newFrames, [sectionFrames[0].id], frameThickness);
    newFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, newFrames, [sectionFrames[0].id], frameThickness);

    newFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, newFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);
    newFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.RIGHT, newFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);

    if (sectionFrames.length > 2) {
      for (let j = 1; j < sectionFrames.length - 1; j++) {
        newFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, newFrames, [sectionFrames[j].id], frameThickness);
        newFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, newFrames, [sectionFrames[j].id], frameThickness);
      }
    }
  }

  return newFrames;
}

function AHUDesigner({ ahuDesignerManager }) {
  const [
    setSelectedUUID,
    toolMode,
    showDimensions,
    frameThickness,
    setFrameThickness,
    setShowAddComponentOptions,
    setSelectedConnectionMenu,
    setFramesAttachedTogether,
    setSelectedUUIDIsInSection,
  ] = useStore((state) => [
    state.setSelectedUUID,
    state.toolMode,
    state.showDimensions,
    state.frameThickness,
    state.setFrameThickness,
    state.setShowAddComponentOptions,
    state.setSelectedConnectionMenu,
    state.setFramesAttachedTogether,
    state.setSelectedUUIDIsInSection,
  ]);

  //setFrameThickness(ahuDesignerManager.frameThickness);

  const [frames, dispatch] = useReducer(frameReducer, convert(ahuDesignerManager));

  useEffect(() => {
    setFrameThickness(ahuDesignerManager.frameThickness);
    dispatch({
      type: "FRAMES",
      value: convert(ahuDesignerManager),
    });

  }, [ahuDesignerManager]);

  // useEffect(() => {
  //   let finalFrames = calculateAttached(frames);

  //   const sectionIds = [...new Set(finalFrames.filter((f) => f.sectionId != null).map((f) => f.sectionId))];

  //   for (let i = 0; i < sectionIds.length; i++) {
  //     const sectionFrames = finalFrames.filter((f) => f.sectionId === sectionIds[i]);

  //     finalFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[0].id], frameThickness);
  //     finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[0].id], frameThickness);

  //     finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);
  //     finalFrames = toggleHasFrame(true, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[sectionFrames.length - 1].id], frameThickness);

  //     if (sectionFrames.length > 2) {
  //       for (let j = 1; j < sectionFrames.length - 1; j++) {
  //         finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.LEFT, finalFrames, [sectionFrames[j].id], frameThickness);
  //         finalFrames = toggleHasFrame(false, COMPONENT_ALIGNMENT.RIGHT, finalFrames, [sectionFrames[j].id], frameThickness);
  //       }
  //     }
  //   }

  //   dispatch({
  //     type: "FRAMES",
  //     value: finalFrames,
  //   });

  // }, []);

  // console.log(frames);

  const [isOrthographic, setIsOrthographic] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cursor, setCursor] = useState("move");
  const [pickup, setPickup] = useState();
  const [draggingEnabled, setDraggingEnabled] = useState(false);
  const [dimensionSide, setDimensionSide] = useState(1);
  const controlsRef = useRef(null);

  function change() {
    if (!controlsRef.current) return;
    const angle = controlsRef.current.getAzimuthalAngle();
    setDimensionSide(angle > 0 ? 1 : -1);
  }

  useEffect(() => {
    if (controlsRef.current) controlsRef.current.autoRotate = autoRotate;
  }, [autoRotate]);

  useEffect(() => {
    setCursor(toolMode == TOOL_MODE.MOVE ? "move" : "default");
  }, [toolMode]);

  function resetControls() {
    controlsRef.current.position0.set(0, 0.25, 8);
    controlsRef.current.target0.set(0, 0.25, 0);
    controlsRef.current.reset();
  }

  function updateUnit(type, id, value) {
    dispatch({ type: type, id: id, value: value });
  }

  const addFrame = (component, position, length, height, width, placing) => {
    const isHeatExchanger = component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER || component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER;
    const isFilter = component === COMPONENT_TYPES.FILTER;

    const newLength = length || (isHeatExchanger ? 750 : 500);
    const newHeight = height || (isHeatExchanger ? 1000 : 500);
    const newWidth = width || 500;

    let newPosition = [0, fromMM(newHeight / 2), 0];

    if (frames.length > 0) {
      const furthestObject = frames.reduce((prev, current) => (prev.position[0] > current.position[0] ? prev : current));
      newPosition = furthestObject.position;
      newPosition[0] += fromMM(furthestObject.length * 1.5);
      newPosition[1] = fromMM(newHeight / 2);
    }

    updateUnit(
      "FRAMES",
      -1,
      frames.concat({
        id: THREE.MathUtils.generateUUID(),
        position: position || newPosition,
        length: newLength,
        height: newHeight,
        width: newWidth,
        connections: connections[component === COMPONENT_TYPES.ROTARY_HEAT_EXCHANGER ? 2 : component === COMPONENT_TYPES.PLATE_HEAT_EXCHANGER ? 1 : 0].map((c) => {
          return { ...c, id: THREE.MathUtils.generateUUID() };
        }),
        hasLeftFrame: !isFilter,
        hasRightFrame: !isFilter,
        hasFrame: true,
        hasFloor: true,
        hasCeiling: true,
        hasLeftWall: false,
        hasRightWall: false,
        component: component,
        //placing: placing == undefined || placing,
      })
    );
  };

  return (
    <>
      <Header
        isOrthographic={isOrthographic}
        setIsOrthographic={setIsOrthographic}
        autoRotate={autoRotate}
        setAutoRotate={setAutoRotate}
        resetControls={resetControls}
        draggingEnabled={draggingEnabled}
        setDraggingEnabled={setDraggingEnabled}
        frames={frames}
        updateUnit={updateUnit}
      />
      <div style={{ display: "flex", cursor: cursor }}>
        <Viewport>
          <Canvas
            flat
            dpr={[1, 2]}
            onPointerMissed={(e) => {
              if (toolMode == TOOL_MODE.SELECTION && !e?.target?.className?.includes("btn-add-component") && !e?.target?.className?.includes("floating-btn")) {
                setSelectedUUID(null);
                setFramesAttachedTogether(false);
                setSelectedUUIDIsInSection(false);
                setShowAddComponentOptions(false);
                setSelectedConnectionMenu(null);
              }
            }}
          >
            <PerspectiveCamera position={[0, 0.25, 8]} fov={25} makeDefault={!isOrthographic} />
            <OrthographicCamera position={[0, 0, 8]} zoom={200} makeDefault={isOrthographic} />
            <ambientLight />
            <pointLight position={[10, 20, 10]} />
            <Dimensions enabled={showDimensions} frames={frames} dimensionSide={dimensionSide}></Dimensions>
            <Unit
              frames={frames}
              draggingEnabled={draggingEnabled}
              setPickup={setPickup}
              pickup={pickup}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              updateUnit={updateUnit}
              addFrame={addFrame}
              test={""}
            />

            <ContactShadows rotation-x={Math.PI / 2} position={[0, 0, 0]} opacity={0.5} length={10} height={10} blur={0.2} far={0.8} />
            <OrbitControls onChange={change} attach={"orbitControls"} makeDefault ref={controlsRef} enabled={!isDragging} target={[0, 0.25, 0]} />
          </Canvas>
        </Viewport>
      </div>
    </>
  );
}

const connections = [
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1],
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1],
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
  ],
  [
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.LEFT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [1, 0],
      direction: DIRECTION_EXTRACT,
      lockedDirection: true,
    },
    {
      side: COMPONENT_ALIGNMENT.RIGHT,
      position: [0, 1],
      direction: DIRECTION_SUPPLY,
      lockedDirection: true,
    },
  ],
];

const example = JSON.parse(
  '[{"id":"24230d7b-7d4e-4864-8e55-56b899ef4c10","position":[-0.994346020755282,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"side":-1,"position":[1],"id":"f1690351-9d1e-4a42-8d75-18be5812e0d0","attachedTo":null,"direction":null},{"side":1,"position":[1],"id":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","attachedTo":"870717d6-64b9-4fe9-bedf-efc00daf90af","direction":null}],"door": { "visible": true }, "hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":true,"hasRightWall":false,"component":-1},{"id":"997eee24-df39-46c6-bb90-449fa74f038d","position":[-0.42434602075528194,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"side":-1,"position":[1],"id":"870717d6-64b9-4fe9-bedf-efc00daf90af","attachedTo":"16133ce5-57fb-4b9e-83f6-d77aeccbc812","direction":null},{"side":1,"position":[1],"id":"a728b63c-79b4-4dae-829c-30b36064192d","attachedTo":"ca882fcf-6529-4e58-9ea2-66f812a6e1a9","direction":null}],"door": { "visible": true },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":-1},{"id":"19c2c70b-4937-4351-90f6-18529e9a754c","position":[0.8406539792447182,0.28850001096725464,0],"length":750,"height":500,"width":500,"connections":[{"id":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","side":-1,"position":[1],"attachedTo":"af646b06-bf12-49cf-8b58-818f26daf553","direction":null},{"id":"bcf64593-7651-4d8c-a854-0c6bb371193e","side":1,"position":[1],"attachedTo":null,"direction":null}],"door": { "visible": true, "length": 400  },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":3,"hasFlange":true,"flangeAlignment":1},{"id":"5a845aa2-8aa8-477e-a4d4-438ea943d224","position":[0.14565397924471812,0.28850001096725464,0],"length":500,"height":500,"width":500,"connections":[{"id":"ca882fcf-6529-4e58-9ea2-66f812a6e1a9","side":-1,"position":[1],"attachedTo":"a728b63c-79b4-4dae-829c-30b36064192d","direction":null},{"id":"af646b06-bf12-49cf-8b58-818f26daf553","side":1,"position":[1],"attachedTo":"218e6c0f-c61f-4fa0-8bf1-2769da1429a4","direction":null}],"door": { "visible": true },"hasLeftFrame":true,"hasRightFrame":true,"hasFrame":true,"hasFloor":true,"hasCeiling":true,"hasLeftWall":false,"hasRightWall":false,"component":-1}]'
);

export default AHUDesigner;
